import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Box, Chip, Paper, Typography } from '@mui/material';
import dashboardApi from '../../services/dashboardApi';
import { MapFeatureProperties } from '../../types/dashboard';
import { COLOR_PALETTE } from '../../utils/emotionUtils';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN || '';
const QOTD_MAP_AUTO_REFRESH_MS = 30 * 1000;

type DashboardQotdMapProps = {
  date: string;
  layer: 'sentiment' | 'density';
  onAreaSelected: (feature: MapFeatureProperties | null) => void;
  fillHeight?: boolean;
};

const numberFormat = new Intl.NumberFormat('en-US');

const DashboardQotdMap = ({ date, layer, onAreaSelected, fillHeight = false }: DashboardQotdMapProps) => {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const popupRef = useRef<mapboxgl.Popup | null>(null);
  const loadViewportDataRef = useRef<() => Promise<void>>(async () => {});
  const onAreaSelectedRef = useRef(onAreaSelected);
  const colorExpressionRef = useRef<any>(null);
  const requestSeqRef = useRef(0);
  const [isLoading, setIsLoading] = useState(true);

  const colorExpression = useMemo(() => {
    if (layer === 'density') {
      return [
        'interpolate',
        ['linear'],
        ['get', 'density_percent'],
        0, '#D6E9FF',
        20, '#B8D8FF',
        40, '#8AB9FF',
        65, '#4F86D9',
        100, '#0A1D45',
      ];
    }
    return ['coalesce', ['get', 'sentiment_color'], '#E1D4C3'];
  }, [layer]);

  const loadViewportData = useCallback(async () => {
    const map = mapRef.current;
    if (!map) return;
    const bounds = map.getBounds();
    if (!bounds) return;

    try {
      const requestId = ++requestSeqRef.current;
      const response = await dashboardApi.getQotdMap({
        date,
        zoom: map.getZoom(),
        bounds: {
          minLng: bounds.getWest(),
          minLat: bounds.getSouth(),
          maxLng: bounds.getEast(),
          maxLat: bounds.getNorth(),
        },
      });

      const currentMap = mapRef.current;
      if (!currentMap || currentMap !== map || !currentMap.isStyleLoaded() || requestId !== requestSeqRef.current) {
        return;
      }

      const rawFeatures = response?.features || [];
      const maxVotes = rawFeatures.reduce((max: number, feature: any) => {
        const votes = Number(feature?.properties?.total_votes || 0);
        return Math.max(max, votes);
      }, 0);
      const enhancedFeatureCollection = {
        type: 'FeatureCollection',
        features: rawFeatures.map((feature: any) => {
          const props = feature.properties || {};
          const totalVotes = Number(props.total_votes || 0);
          const goodVotes = Number(props.good_votes || 0);
          const positivePercentage = totalVotes > 0 ? (goodVotes / totalVotes) * 100 : 50;
          const densityPercent = maxVotes > 0 ? (totalVotes / maxVotes) * 100 : 0;
          return {
            ...feature,
            properties: {
              ...props,
              positive_percentage: Number(positivePercentage.toFixed(1)),
              density_percent: Number(densityPercent.toFixed(1)),
              density_label: `${densityPercent.toFixed(0)}%`,
              sentiment_color:
                totalVotes > 0
                  ? COLOR_PALETTE.find(
                      (entry) => positivePercentage >= entry.range[0] && positivePercentage < entry.range[1]
                    )?.color || '#E1D4C3'
                  : '#E1D4C3',
            },
          };
        }),
      };

      const source = currentMap.getSource('qotd-areas') as mapboxgl.GeoJSONSource | undefined;
      if (source) {
        source.setData(enhancedFeatureCollection as any);
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading QOTD map data', error);
      setIsLoading(false);
    }
  }, [date]);

  useEffect(() => {
    loadViewportDataRef.current = loadViewportData;
  }, [loadViewportData]);

  useEffect(() => {
    onAreaSelectedRef.current = onAreaSelected;
  }, [onAreaSelected]);

  useEffect(() => {
    colorExpressionRef.current = colorExpression;
  }, [colorExpression]);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;
    if (!mapboxgl.accessToken) {
      setIsLoading(false);
      return;
    }

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: [-73.9712, 40.7831],
      zoom: 10,
      minZoom: 7,
      maxZoom: 18,
    });
    mapRef.current = map;

    map.on('load', () => {
      map.addSource('qotd-areas', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });

      map.addLayer({
        id: 'qotd-areas-fill',
        type: 'fill',
        source: 'qotd-areas',
        paint: {
          'fill-color': colorExpressionRef.current as any,
          'fill-opacity': 0.75,
        },
      });

      map.addLayer({
        id: 'qotd-areas-outline',
        type: 'line',
        source: 'qotd-areas',
        paint: {
          'line-color': '#dddddd',
          'line-width': 0.6,
          'line-opacity': 0.4,
        },
      });

      map.addLayer({
        id: 'qotd-density-labels',
        type: 'symbol',
        source: 'qotd-areas',
        layout: {
          'text-field': ['get', 'density_label'],
          'text-size': 12,
          'text-allow-overlap': true,
          visibility: 'none',
        },
        paint: {
          'text-color': '#DDE9FF',
          'text-halo-color': 'rgba(10, 18, 36, 0.8)',
          'text-halo-width': 1.2,
        },
      });

      loadViewportDataRef.current();
      window.setTimeout(() => map.resize(), 0);
    });

    const moveHandler = () => {
      window.clearTimeout((moveHandler as any)._timeout);
      (moveHandler as any)._timeout = window.setTimeout(() => {
        loadViewportDataRef.current();
      }, 350);
    };
    map.on('moveend', moveHandler);

    map.on('mousemove', 'qotd-areas-fill', (event) => {
      const feature = event.features?.[0];
      if (!feature) return;
      const properties = feature.properties as unknown as MapFeatureProperties;

      if (!popupRef.current) {
        popupRef.current = new mapboxgl.Popup({
          closeButton: false,
          closeOnClick: false,
          className: 'area-tooltip',
        });
      }
      const votes = Number(properties.total_votes || 0);
      const goodVotes = Number(properties.good_votes || 0);
      const badVotes = Number(properties.bad_votes || 0);
      const goodPct = votes > 0 ? ((goodVotes / votes) * 100).toFixed(1) : '0.0';
      const badPct = votes > 0 ? ((badVotes / votes) * 100).toFixed(1) : '0.0';
      const density = properties.density_percent != null ? `${Number(properties.density_percent).toFixed(1)}%` : '0.0%';

      popupRef.current
        .setLngLat(event.lngLat)
        .setHTML(
          `<div style="min-width:200px;padding:10px;color:#ffffff">
            <div style="font-weight:600;margin-bottom:6px">${properties.name}</div>
            <div>Sentiment Index: <strong>${Number(properties.sentiment_index || 0).toFixed(1)}</strong></div>
            <div>Votes: <strong>${numberFormat.format(votes)} votes</strong></div>
            <div><strong>${goodPct}%</strong> positive · <strong>${badPct}%</strong> negative</div>
            <div>Vote density: <strong>${density}</strong></div>
          </div>`
        )
        .addTo(map);
    });

    map.on('mouseleave', 'qotd-areas-fill', () => {
      popupRef.current?.remove();
    });

    map.on('click', 'qotd-areas-fill', (event) => {
      const feature = event.features?.[0];
      if (!feature) return;
      onAreaSelectedRef.current(feature.properties as unknown as MapFeatureProperties);
    });

    const refreshInterval = window.setInterval(() => {
      if (document.hidden) return;
      loadViewportDataRef.current();
    }, QOTD_MAP_AUTO_REFRESH_MS);

    return () => {
      window.clearInterval(refreshInterval);
      popupRef.current?.remove();
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (map.getLayer('qotd-areas-fill')) {
      map.setPaintProperty('qotd-areas-fill', 'fill-color', colorExpression as any);
      map.setPaintProperty('qotd-areas-fill', 'fill-opacity', layer === 'density' ? 0.62 : 0.75);
    }
    if (map.getLayer('qotd-density-labels')) {
      map.setLayoutProperty('qotd-density-labels', 'visibility', layer === 'density' ? 'visible' : 'none');
    }
    loadViewportData();
  }, [colorExpression, loadViewportData, layer, date]);

  return (
    <Paper
      className="dashboard-section-card"
      sx={{ p: 2, borderRadius: 2, height: fillHeight ? '100%' : 'auto', display: 'flex', flexDirection: 'column' }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1 }}>
        <Typography variant="h6" sx={{ color: '#e8edf4' }}>Map Drilldown</Typography>
        <Chip
          size="small"
          color={layer === 'sentiment' ? 'success' : 'primary'}
          label={layer === 'sentiment' ? 'Sentiment Layer' : 'Vote Density Layer'}
        />
      </Box>
      <Box
        ref={mapContainerRef}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
          height: fillHeight ? '100%' : 480,
          minHeight: fillHeight ? 0 : 480,
          flex: fillHeight ? 1 : 'unset',
          borderRadius: 2,
          border: '1px solid rgba(255,255,255,0.10)',
          opacity: isLoading ? 0.65 : 1,
        }}
      />
    </Paper>
  );
};

export default DashboardQotdMap;
